/**
 * DnsConflictBanner — Sub-projeto 4 Lote 2.
 *
 * Renderizado em Step4Review quando preflight check #10 (`dns_conflict`)
 * detecta record A/CNAME legacy apontando direto pro ALB do tenant.
 *
 * Default: checkbox MARCADO (admin opta-out se quiser path antigo).
 * Edita upstream_url + radio protocol (https/http).
 *
 * Estado persistido em ``wizardState.dnsMigration`` e enviado no payload
 * do POST /onboard. Lote 3 fara o pipeline_steps consumir esse opt-in pra
 * branch MIGRATE.
 */
import { useEffect } from 'react'
import {
  Alert,
  Checkbox,
  Code,
  Group,
  Radio,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'

import type { LegacyDnsConflict } from '../types'
import type { DnsMigrationState } from './wizardState'

interface Props {
  conflict: LegacyDnsConflict
  state: DnsMigrationState | undefined
  onChange: (next: DnsMigrationState) => void
}

export function DnsConflictBanner({ conflict, state, onChange }: Props) {
  const enabled = state?.enabled ?? true
  const upstream =
    state?.upstream_url ?? conflict.proposed_upstream_url ?? ''
  const protocol: 'https' | 'http' = state?.protocol ?? conflict.proposed_protocol

  // Populate state com defaults quando undefined no mount. Sem isso, o
  // checkbox aparece marcado mas state.dnsMigration nunca eh enviado no
  // POST /onboard, e o backend cai na branch NEW (bug 2026-05-12).
  useEffect(() => {
    if (state === undefined) {
      onChange({
        enabled: true,
        upstream_url: conflict.proposed_upstream_url ?? '',
        protocol: conflict.proposed_protocol,
        record_checksum: conflict.record_checksum ?? undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const emit = (patch: Partial<DnsMigrationState>) => {
    onChange({
      enabled,
      upstream_url: upstream,
      protocol,
      record_checksum: conflict.record_checksum ?? undefined,
      ...patch,
    })
  }

  return (
    <Alert
      icon={<IconAlertTriangle size={20} />}
      color="yellow"
      title="A record legacy detectado"
      data-testid="dns-conflict-banner"
    >
      <Stack gap="xs">
        <Text size="sm">
          Hostname já tem registro DNS apontando direto pra{' '}
          <Code>{conflict.target ?? '—'}</Code> ({conflict.record_type}). Marque
          a opção abaixo pra migrar pro ALB DevOpsSys (broker).
        </Text>
        <Checkbox
          checked={enabled}
          onChange={(e) => emit({ enabled: e.currentTarget.checked })}
          label="Migrar registro pro ALB DevOpsSys (broker)"
          data-testid="dns-migrate-checkbox"
        />
        {enabled && (
          <Group grow align="flex-start">
            <TextInput
              label="Upstream proposto"
              value={upstream}
              onChange={(e) => emit({ upstream_url: e.currentTarget.value })}
              data-testid="dns-upstream-input"
            />
            <Radio.Group
              label="Protocol"
              value={protocol}
              onChange={(v) => emit({ protocol: v as 'https' | 'http' })}
            >
              <Group gap="xs" mt={6}>
                <Radio value="https" label="HTTPS" data-testid="dns-protocol-https" />
                <Radio value="http" label="HTTP" data-testid="dns-protocol-http" />
              </Group>
            </Radio.Group>
          </Group>
        )}
      </Stack>
    </Alert>
  )
}
